import { useEffect, useState } from "react";
import NewRepairPage from "./MobileNewRepairPage.jsx";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useInput, inputsHasError } from "../../utils/hooks";
import { observer, inject } from "mobx-react";
import { isNotEmpty, withError } from "../../validation";
import { format } from "date-fns";

const NewApply = ({ userStore, tenantsStore, repairPagesStore }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [defectList, setDefectList] = useState(
    !!repairPagesStore.defectList ? [...repairPagesStore?.defectList] : []
  );
  const [doValidation, setDoValidation] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { t } = useTranslation("applies");
  const location = useLocation();
  const [department, setDepartment] = useState(location?.state?.department);
  const [newRepairPage, setNewRepairPage] = useState({
    APPLICANTNAME: userStore.userDetails.NAME,
    SITENUM:
      tenantsStore.selectedContract?.SITENUM ||
      tenantsStore.selectedSiteSpace?.SITENUM ||
      location?.state?.SITENUM,
    BUILDINGNUM:
      tenantsStore.selectedContract?.BUILDINGNUM ||
      tenantsStore.selectedSiteSpace?.BUILDINGNUM ||
      location?.state?.BUILDINGNUM,
    SPACENUM:
      tenantsStore.selectedContract?.SPACENUM ||
      tenantsStore.selectedSiteSpace?.SPACENUM ||
      location?.state?.SPACENUM,
    SPACENAME:
      tenantsStore.selectedContract?.SPACENAME ||
      tenantsStore.selectedSiteSpace?.SPACENAME ||
      location?.state?.SPACENAME,
    CURDATE: new Date(),
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = {
        CUST: userStore.userDetails.CUST,
        MAINENV: userStore.userDetails.MAINENV,
        DNAME: tenantsStore.selectedContract?.DNAME,
        ENV: tenantsStore.selectedContract?.DNAME,
      };
      await repairPagesStore.getDomains(data);
      await repairPagesStore.getLocations(data);
      await repairPagesStore.getSiteAreas(data);
      setLoading(false);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDepartment(location?.state?.department);
  }, [location]);

  const handleDateChange = (date) => {
    var localDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60 * 1000
    );

    setNewRepairPage({ ...newRepairPage, CURDATE: localDate });
  };

  const handleChange = (prop) => (event) => {
    setNewRepairPage({ ...newRepairPage, [prop]: event.target.value });
  };

  const handleDefectChange = (defect) => {
    const newDefects = [...defectList];

    const defectIndex = newDefects.findIndex((d) => {
      return defect.KLINE === d.KLINE && d.index === defect.index;
    });

    newDefects[defectIndex] = defect;
    const defectsDeepCopy = JSON.parse(
      JSON.stringify(newDefects, (key, value) =>
        value === undefined ? null : value
      )
    );
    const list = defectsDeepCopy.filter((defect) => defect.REMOVE !== true);

    setDefectList([...list]);
    repairPagesStore.setDefectList([...list]);
  };

  const validateDefecs = (newDiaryData) => {
    if (
      !newDiaryData.ZCOB_DEFECTSLIST_SUBFORM ||
      newDiaryData.ZCOB_DEFECTSLIST_SUBFORM.length === 0
    ) {
      setError(t("empty_defect_list"));
      return false;
    }
    const hasEmpty = newDiaryData.ZCOB_DEFECTSLIST_SUBFORM.some((p) => {
      return (
        p?.REMOVE !== true &&
        (!p?.DEFECTDES ||
          !p?.SITEAREADES ||
          !p?.LOCATIONDES ||
          !p?.CHKPERIODTYPECODE)
      );
    });

    return !hasEmpty;
  };

  const handleAddApply = async () => {
    const repairData = {
      ...newRepairPage,
      ZCOB_DEFECTSLIST_SUBFORM: defectList
        .filter((defect) => defect.REMOVE !== true)
        .map((d) => {
          delete d.APPROVE;
          delete d.CURDATE;
          delete d.NAME;
          delete d.index;
          return d;
        }),
    };

    const body = {
      MAINCUST: userStore.userDetails.MAINCUST,
      CUST: userStore.userDetails.CUST,
      MAINENV: userStore.userDetails.MAINENV,
      DNAME: tenantsStore.selectedContract?.DNAME,
      ENV: tenantsStore.selectedContract?.DNAME,
      CONTNAME: tenantsStore.selectedContract?.CONTNAME,
      SPACENAME:
        tenantsStore.selectedContract?.SPACENAME ||
        tenantsStore.selectedSiteSpace?.SPACENAME ||
        location?.state?.SPACENAME,
      CONT: tenantsStore.selectedContract?.CONT,
      DNAME: tenantsStore.selectedContract?.DNAME,
      ENV: tenantsStore.selectedContract?.DNAME,
    };
    const defectValid = validateDefecs(repairData);
    setDoValidation(true);
    if (!inputsHasError(date, applier) && defectValid) {
      setSaving(true);
      const repairPage = await repairPagesStore.addRepairPage({
        ...body,
        data: repairData,
      });

      if (repairPagesStore.error) {
        setError(repairPagesStore.error);
      } else {
        const initstat = repairPagesStore.statuses?.find(
          (s) => s.INITPORTALFLAG === "Y"
        )?.STATDES;
        if (!!initstat)
          await repairPagesStore.updateRepairPage({
            ...body,
            REPAIRNAME: repairPage?.REPAIRNAME,
            data: { STATDES: initstat },
          });
        setShowSuccessModal(true);
      }
      repairPagesStore.setDefectList([]);
      setSaving(false);
    }
  };

  const isNotEmptyWithError = withError(
    isNotEmpty,
    t("text_field_empty_error")
  );

  const applier = useInput({
    validation: [isNotEmptyWithError],
    placeholder: t("applier_placeholder"),
    value: newRepairPage?.APPLICANTNAME || "",
  });

  const date = useInput({
    validation: [isNotEmptyWithError],
    placeholder: t("date_placeholder"),
    value: format(
      newRepairPage?.CURDATE !== undefined
        ? new Date(newRepairPage.CURDATE)
        : new Date(),
      "dd/MM/yyyy"
    ),
  });

  return (
    <div>
      <NewRepairPage
        setShowSuccessModal={setShowSuccessModal}
        showSuccessModal={showSuccessModal}
        isSaving={isSaving}
        handleAddApply={handleAddApply}
        error={error}
        doValidation={doValidation}
        handleDefectChange={handleDefectChange}
        date={date}
        applier={applier}
        handleDateChange={handleDateChange}
        department={department}
        items={items}
        setItems={setItems}
        defectList={defectList}
        handleChange={handleChange}
      />
    </div>
  );
};

export default inject(
  "userStore",
  "tenantsStore",
  "repairPagesStore"
)(observer(NewApply));
